<script setup lang="ts">
   import { ref } from 'vue';
   import PoloDevPlatformNavList from '~/components/PoloDevPlatformNavList.vue';
   import { useScrollNav } from '~/composables/useScrollNav';
   import { usePdpNavigation } from '~/composables/usePdpNavigation';
   import { SiteRouteNames } from '~/types/types';

   const poloDevPlatformNavListRef = ref<{ openMenu: () => void } | null>(null);
   const { isVisible, isScrolled } = useScrollNav();
   const { visibleItems } = usePdpNavigation();

   function openMobileMenu() {
      if (poloDevPlatformNavListRef.value) {
         poloDevPlatformNavListRef.value.openMenu();
      }
   }
</script>

<template>
   <div
      class="fixed top-0 z-50 flex w-full flex-col items-center justify-center bg-white pb-3 transition-all duration-300 lg:px-3"
      :class="isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-2 pointer-events-none'"
      style="box-shadow: 1px 1px 16px 0 rgba(0, 0, 0, 0.24)">
      <div class="w-full max-w-home-page-header-content">
         <!-- Top centered text - desktop only, hidden when scrolled -->
         <div
            class="hidden py-4 transition-all duration-300 lg:flex lg:flex-col lg:items-center lg:justify-center"
            :class="[
               isScrolled
                  ? 'lg:max-h-0 lg:overflow-hidden lg:py-0 lg:opacity-0'
                  : 'lg:max-h-20 lg:opacity-100',
            ]"
            data-testid="pdp-header-text-top">
            <TextLogo variant="centered" />
         </div>

         <nav class="flex w-full items-center p-3 lg:justify-center lg:p-0">
            <!-- Left text - appears when scrolled -->
            <div
               class="transition-all duration-300 lg:py-2"
               :class="[
                  !isScrolled
                     ? 'lg:max-w-0 lg:overflow-hidden lg:opacity-0'
                     : 'lg:mr-6 lg:max-w-full lg:opacity-100',
               ]"
               data-testid="pdp-header-text-left">
               <TextLogo
                  variant="left"
                  title-size="whitespace-nowrap text-lg" />
            </div>

            <!-- Desktop Menu -->
            <ul class="ml-auto hidden items-center gap-3 lg:ml-0 lg:flex">
               <template v-for="item in visibleItems" :key="item.label">
                  <!-- Back to Home Link - Special styling for first item -->
                  <li v-if="item.label === 'Back to Home'">
                     <NuxtLink
                        :to="item.to"
                        class="flex items-center gap-2 px-4 py-3 text-brand-title transition-colors duration-200 hover:text-brand-title"
                        :title="item.title"
                        :aria-label="item.title"
                        data-testid="pdp-back-to-home-desktop">
                        <i v-if="item.icon" :class="`pi ${item.icon}`" style="font-size: 1.25rem;"></i>
                        <span class="font-semibold">{{ item.label }}</span>
                     </NuxtLink>
                  </li>

                  <template v-else>
                  <!-- Dropdown menu item -->
                  <li v-if="item.items && item.items.length > 0" class="group relative flex">
                     <span
                        :title="item.title"
                        class="flex cursor-pointer items-center justify-center gap-1 whitespace-nowrap border border-white bg-brand-secondary px-7 py-3 uppercase text-white">
                        <i v-if="item.icon" :class="`pi ${item.icon} mr-1`" style="font-size: 1rem !important;"></i>
                        {{ item.label }}
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="size-4">
                           <path fill-rule="evenodd" d="M12.53 16.28a.75.75 0 0 1-1.06 0l-7.5-7.5a.75.75 0 0 1 1.06-1.06L12 14.69l6.97-6.97a.75.75 0 1 1 1.06 1.06l-7.5 7.5Z" clip-rule="evenodd" />
                        </svg>
                     </span>
                     <ul class="pointer-events-none absolute left-0 top-full z-50 min-w-64 border border-gray-200 bg-white pt-3 opacity-0 shadow-lg transition-all duration-200 group-hover:pointer-events-auto group-hover:opacity-100">
                        <li v-for="(subItem, index) in item.items" :key="subItem.label">
                           <NuxtLink
                              v-if="subItem.to"
                              :to="subItem.to"
                              class="block px-4 py-3 text-sm font-semibold uppercase text-gray-700 hover:bg-brand-secondary hover:text-white"
                              :class="{ 'border-b border-gray-100': index < item.items.length - 1 }">
                              {{ subItem.label }}
                           </NuxtLink>
                           <button
                              v-else
                              class="block w-full px-4 py-3 text-left text-sm font-semibold uppercase text-gray-700 hover:bg-brand-button hover:text-white"
                              :class="{ 'border-b border-gray-100': index < item.items.length - 1 }">
                              {{ subItem.label }}
                           </button>
                        </li>
                     </ul>
                  </li>

                  <!-- Regular menu item -->
                  <li v-else-if="item.to">
                     <NuxtLink
                        :to="item.to"
                        :title="item.title"
                        class="flex cursor-pointer items-center justify-center whitespace-nowrap border border-white bg-brand-secondary px-7 py-3 uppercase text-white transition-colors duration-200 hover:bg-brand-title">
                        <i v-if="item.icon" :class="`pi ${item.icon} mr-1`" style="font-size: 1rem !important;"></i>
                        {{ item.label }}
                     </NuxtLink>
                  </li>
                  </template>
               </template>
            </ul>

            <!-- Mobile Menu Button -->
            <button
               @click="openMobileMenu"
               class="ml-auto flex items-center justify-center border border-white bg-brand-button px-4 py-3 text-white lg:hidden"
               aria-label="Open mobile menu">
               <svg class="size-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16"></path>
               </svg>
            </button>
         </nav>
      </div>
   </div>

   <!-- Mobile Menu (Hidden by default, controlled by Mmenu) -->
   <div class="lg:hidden">
      <PoloDevPlatformNavList ref="poloDevPlatformNavListRef" />
   </div>
</template>

<style scoped></style>
